import type { ContextType, ModuleAccess, UserContext } from '@vanialabs/core-permissions';
import type { LoginResponseDTO } from './login';

const REPRESENTATIVE_CONTEXT_TYPE: ContextType = 'representative';

/**
 * Maps a representative login response to the UserContext[] shape defined
 * in @vanialabs/core-permissions.
 *
 * Scope for this phase (per approved architecture decision, 2026-08-07):
 *  - `modules` is always [] — no real permission resolution yet. Populating
 *    it correctly would require per-role permissions that the backend's
 *    LoginResponseDTO.roles does not currently include (raw role rows only:
 *    id/name/slug/description — no resolved permissions per role). Wiring
 *    that up is deferred to a later phase and is out of scope here.
 *  - `id` mirrors the exact `representative:${id}` format toAuthSession()
 *    already writes into AuthSession.activeContextId, so a context can
 *    always be looked up by session.activeContextId without reformatting
 *    on either side.
 *  - tenantId is intentionally NOT added to UserContext this phase (approved
 *    decision to keep the current core-permissions type contract unchanged).
 *
 * Pure function: no core-auth import, no store access, no side effects.
 * `response.roles` is intentionally unused this phase (see above).
 */
export function toUserContexts(response: LoginResponseDTO): UserContext[] {
  const representative = response.representative;

  const name =
    representative.full_name?.trim() ||
    representative.mobile;

  const modules: ModuleAccess[] = [];

  return [
    {
      id: `representative:${representative.id}`,
      type: REPRESENTATIVE_CONTEXT_TYPE,
      name,
      modules,
    },
  ];
}
